import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "projects/ert/src/environments/environment";
import {
  IPreplanRescue,
  PortalSize,
  PreplanConfiguration,
} from "../../interfaces/ert-preplan-rescue/ert-preplan-rescue";
import { Observable } from "rxjs";
import { FormGroup } from "@angular/forms";

@Injectable({
  providedIn: "root",
})
export class PreplanRescueService {
  private apiUrl = environment.apiUrl + "/preplan-rescue";
  constructor(private http: HttpClient) {}

  public getPreplanRescue(facilityId: Number): Observable<IPreplanRescue[]> {
    return this.http.get<IPreplanRescue[]>(`${this.apiUrl}/${facilityId}`);
  }

  public getPreplanRescueById(
    facilityId: Number,
    id: number
  ): Observable<IPreplanRescue> {
    return this.http.get<IPreplanRescue>(`${this.apiUrl}/${facilityId}/${id}`);
  }

  public addPreplanRescue(
    preplanRescue: IPreplanRescue
  ): Observable<IPreplanRescue> {
    return this.http.post<IPreplanRescue>(`${this.apiUrl}`, preplanRescue);
  }

  public updatePreplanRescue(
    preplanRescue: IPreplanRescue
  ): Observable<IPreplanRescue> {
    return this.http.put<IPreplanRescue>(`${this.apiUrl}/`, preplanRescue);
  }

  public deletePreplanRescue(
    facilityId: Number,
    id: number
  ): Observable<IPreplanRescue> {
    return this.http.delete<IPreplanRescue>(
      `${this.apiUrl}/${facilityId}/${id}`
    );
  }

  // Helper methods to convert enum values to string names
  private getPortalSizeName(portalSize: PortalSize): string {
    switch (portalSize) {
      case PortalSize.LessThan24:
        return "Less Than 24 inches";
      case PortalSize.MoreThan24:
        return "More Than 24 inches";
      default:
        return "";
    }
  }

  private getConfigurationName(configuration: PreplanConfiguration): string {
    switch (configuration) {
      case PreplanConfiguration.RoundOval:
        return "Round/Oval";
      case PreplanConfiguration.SquareRectangle:
        return "Square/Rectangle";
      default:
        return "";
    }
  }

  public printPreplanRescue(
    preplanRescue: IPreplanRescue,
    facilityName: string
  ): void {
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Confined Space Rescue Pre-Plan</title>
            <meta charset="utf-8">
            <style>
              .gray-background {
                background-color: lightgray !important;
                padding: 1px;
                margin: 1px;
              }
            </style>
          </head>
          <body style="font-family: Arial, sans-serif; font-size: 12px; padding: 20px; margin: 0;">
            <div style="width: 100%; max-width: 800px; margin: 0 auto;">
              <h2 style="text-align: center; margin: 10px 0; font-size: 16px;">Chevron ${facilityName} Refinery - Confined Space Rescue Pre-Plan</h2>
              
              <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px;">
                <tr style="background-color: silver; font-weight: bold;">
                  <td colspan="6">Section 1 - Completed by Maintenance</td>
                </tr>
                <tr>
                  <td style="width:15%;">Date:</td>
                  <td style="width:10%; border: 1px solid black; font-size: 12px;">${new Date(
                    preplanRescue.dateTimeAdd
                  ).toLocaleDateString()}</td>
                  <td style="width: 3%;"></td>
                  <td style="width:20%;">Reason for Entry</td>
                  <td style="width:22%; border: 1px solid black; font-size: 12px;">${
                    preplanRescue.reasonOfEntry
                  }</td>
                  <td style="width:1%;"></td>
                </tr>
                <tr>
                  <td style="width:15%;">Duration of Work:</td>
                  <td style="width:10%; border: 1px solid black; font-size: 12px;">${
                    preplanRescue.durationOfWork
                  }</td>
                  <td style="width: 3%;"></td>
                  <td style="width:20%;">Maints Rep/Organizer</td>
                  <td style="width:22%; border: 1px solid black; font-size: 12px;">${
                    preplanRescue.maintsRepOriginator
                  }</td>
                  <td style="width:1%;"></td>
                </tr>
              </table>

              <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px;">
                <tr style="background-color: silver; font-weight: bold;">
                  <td colspan="6">Section 2 - Completed by Operations</td>
                </tr>
                <tr>
                  <td style="width:15%;">Area STL:</td>
                  <td style="width:25%; border: 1px solid black; font-size: 12px;">${
                    preplanRescue.areaSTL || ""
                  }</td>
                  <td style="width: 3%;"></td>
                  <td style="width:15%;">Area HO</td>
                  <td style="width:25%; border: 1px solid black; font-size: 12px;">${
                    preplanRescue.areaHO || ""
                  }</td>
                  <td style="width:1%;"></td>
                </tr>
                <tr>
                  <td colspan="3">Hazard Assessment (Obstacles, Engulfment, PPE, etc.)</td>
                  <td colspan="3">Description of Confined Space to be entered (Number Height, Diameter)</td>
                </tr>
                <tr style="height: 125px;">
                  <td colspan="3" style="border: 1px solid black; font-size: 12px;">${
                    preplanRescue.hazardAssignments || ""
                  }</td>
                  <td colspan="3" style="border: 1px solid black; font-size: 12px;">${
                    preplanRescue.descriptionConfinedSpace || "&nbsp;"
                  }</td>
                </tr>
              </table>

              <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px;">
                <tr style="background-color: silver; font-weight: bold;">
                  <td colspan="6">Section 3 - Completed by Chevron Rescue Technician</td>
                </tr>
                <tr class="gray-background">
                  <td colspan="3">Space Category</td>
                  <td colspan="3">Medical Packaging Information</td>
                </tr>
                <tr>
                  <td style="width: 1%; border: 1px solid black; text-align: center;">${
                    preplanRescue.spaceCategory?.isRa ? "X" : ""
                  }</td>
                  <td style="width: 25%;">Category 1 . . . Rescue Available (RA)</td>
                  <td style="width: 1%; border: 1px solid black; text-align: center;">${
                    preplanRescue.medicalPackingInfo?.isLongSpineBoard
                      ? "X"
                      : ""
                  }</td>
                  <td style="width: 25%;">Long Spine Board</td>
                </tr>
                <tr>
                  <td style="width: 1%; border: 1px solid black; text-align: center;">${
                    preplanRescue.spaceCategory?.isRs ? "X" : ""
                  }</td>
                  <td style="width: 25%;">Category 2 . . . Rescue Stand-By (RS)</td>
                  <td style="width: 1%; border: 1px solid black; text-align: center;">${
                    preplanRescue.medicalPackingInfo?.isKED ? "X" : ""
                  }</td>
                  <td style="width: 25%;">Half Back (KED)</td>
                </tr>
                <tr>
                  <td colspan="2"></td>
                  <td style="width: 1%; border: 1px solid black; text-align: center;">${
                    preplanRescue.medicalPackingInfo?.isStokesBasket ? "X" : ""
                  }</td>
                  <td style="width: 25%;">Stokes Basket</td>
                </tr>
                <tr>
                  <td colspan="2" style="background-color: silver; font-weight: bold;">Space Information</td>
                  <td style="width: 1%; border: 1px solid black; text-align: center;">${
                    preplanRescue.medicalPackingInfo?.isBreathingAirRequired
                      ? "X"
                      : ""
                  }</td>
                  <td style="width: 25%;">Breathing Air Required</td>
                </tr>
                <tr>
                  <td colspan="2" style="border: 1px solid black; font-size: 12px; height: 50px;">${
                    preplanRescue.spaceInformation || "&nbsp;"
                  }</td>
                  <td colspan="2"></td>
                </tr>
              </table>

              <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px;">
                <tr class="gray-background">
                  <td colspan="2">How is it accessed?</td>
                  <td colspan="2">Rescue Method Haul System</td>
                </tr>
                <tr>
                  <td style="width: 1%; border: 1px solid black; text-align: center;">${
                    preplanRescue.accessMethod?.isHorizontal ? "X" : ""
                  }</td>
                  <td style="width: 25%;">Horizontal</td>
                  <td style="width: 1%; border: 1px solid black; text-align: center;">${
                    preplanRescue.rescueMethodHaulSystem?.isExternalRetrieval
                      ? "X"
                      : ""
                  }</td>
                  <td style="width: 25%;">External Retrieval</td>
                </tr>
                <tr>
                  <td style="width: 1%; border: 1px solid black; text-align: center;">${
                    preplanRescue.accessMethod?.isVerticalTop ? "X" : ""
                  }</td>
                  <td style="width: 25%;">Vertical Top</td>
                  <td style="width: 1%; border: 1px solid black; text-align: center;">${
                    preplanRescue.rescueMethodHaulSystem?.is31 ? "X" : ""
                  }</td>
                  <td style="width: 25%;">3.1</td>
                </tr>
                <tr>
                  <td style="width: 1%; border: 1px solid black; text-align: center;">${
                    preplanRescue.accessMethod?.isVerticalBottom ? "X" : ""
                  }</td>
                  <td style="width: 25%;">Vertical Bottom</td>
                  <td style="width: 1%; border: 1px solid black; text-align: center;">${
                    preplanRescue.rescueMethodHaulSystem?.isInternalRetrieval
                      ? "X"
                      : ""
                  }</td>
                  <td style="width: 25%;">Internal Retrieval</td>
                </tr>
                <tr>
                  <td style="width: 1%; border: 1px solid black; text-align: center;">${
                    preplanRescue.accessMethod?.isAccessElevated ? "X" : ""
                  }</td>
                  <td style="width: 25%;">Is Access Elevated 4 ft. above grade?</td>
                  <td style="width: 1%; border: 1px solid black; text-align: center;">${
                    preplanRescue.rescueTechDescription ? "X" : ""
                  }</td>
                  <td style="width: 25%;">Rescue Tech Discretion</td>
                </tr>
                <tr>
                  <td colspan="3"></td>
                  <td style="border: 1px solid black; font-size: 12px; height: 50px;">${
                    preplanRescue.rescueTechDescription || "&nbsp;"
                  }</td>
                </tr>
              </table>

              <table style="width: 100%; border-collapse: collapse; margin-bottom: 15px;">
                <tr>
                  <td style="width:10%;">Portal Size:</td>
                  <td style="width:25%; border: 1px solid black;">${
                    preplanRescue.portalSize !== undefined
                      ? this.getPortalSizeName(preplanRescue.portalSize)
                      : ""
                  }</td>
                  <td colspan="2"></td>
                </tr>
                <tr>
                  <td style="width:10%;">Configuration:</td>
                  <td style="width:25%; border: 1px solid black;">${
                    preplanRescue.configuration !== undefined
                      ? this.getConfigurationName(preplanRescue.configuration)
                      : ""
                  }</td>
                  <td colspan="2"></td>
                </tr>
              </table>
            </div>
            <script>
              window.onload = function() {
                window.print();
                window.addEventListener('afterprint', function() {
                  window.close();
                });
                setTimeout(function() {
                  window.opener = null;
                }, 1000);
              }
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();

      const cleanup = () => {
        try {
          if (printWindow && !printWindow.closed) {
            printWindow.opener = null;
          }
        } catch (e) {
          console.log("Print window cleanup error:", e);
        }
      };

      setTimeout(cleanup, 2000);
    } else {
      console.error("Could not open print window");
    }
  }

  validatePreplanRescueForm(preplanRescueForm: FormGroup<any>): string[] {
    let validationMessages = [];

    const controls = preplanRescueForm.controls;

    if (controls["date"].invalid) {
      validationMessages.push("Date is required");
    }
    if (controls["durationOfWork"].invalid) {
      validationMessages.push("Duration of Work is required");
    }
    if (controls["reasonForEntry"].invalid) {
      validationMessages.push("Reason for Entry is required");
    }
    if (controls["maintsRep"].invalid) {
      validationMessages.push("Maints Rep/Originator is required");
    }

    return validationMessages;
  }
}
